import {
  FC,
  MouseEvent,
  MouseEventHandler,
  MutableRefObject,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@microservices/wiskey-react-components'
import {
  GridColumnHeaderParams,
  GridColumnHeaderTitle,
} from '@microservices/wiskey-react-table-component'
import { GridApiPro } from '@microservices/wiskey-react-table-component'
import { Edit } from '@mui/icons-material'
import { Box } from '@mui/material'

import { PageContext } from '@pages/ConfiguredEntity/ConfiguredEntity'
import { getIsAvailableRuleByType } from '@pages/ConfiguredEntity/helpers'

import { useSubscribeEvent } from '@hooks'
import { FIELD_VALUE_TYPE, MIN_VIEW_COLUMN_WIDTH } from '@constants'
import {
  CHECKBOX_IN_COLUMN_FOR_SEARCHING_EVENT_DETAIL,
  ConfiguredColumn,
  GLOBAL_CHECKBOX_IN_COLUMN_EVENT_DETAIL,
  ObjectDataRecord,
} from '@types'

import { EVENT_NAME, publish } from '../../../../events'

type HeaderRenderType = {
  column: ConfiguredColumn
  params: GridColumnHeaderParams<any, ObjectDataRecord, any>
  apiRef: MutableRefObject<GridApiPro>
  hasQuickSearch: boolean | undefined
  hasWrapHeaderText: boolean
  isEnabledLocalParameters: boolean
  showEditMarker: boolean
  handleOpenTooltip: (event: MouseEvent<HTMLElement>, isHeader?: boolean) => void
  handleCloseTooltip: (event: MouseEvent<HTMLElement>) => void
}

export const HeaderRender: FC<HeaderRenderType> = ({
  column,
  params,
  apiRef,
  hasQuickSearch = true,
  hasWrapHeaderText = false,
  isEnabledLocalParameters = false,
  showEditMarker,
  handleOpenTooltip,
  handleCloseTooltip,
}) => {
  const { t } = useTranslation()
  const sortModel = apiRef.current?.getSortModel()[0]
  const isSortableField = sortModel?.field === column?.code
  const columnHeaderTextWidth = params?.colDef?.width ? params.colDef.width : MIN_VIEW_COLUMN_WIDTH

  const {
    columnNameCheckBox,
    searchRule,
    valueSearchFilter,
    sortKeysColumnCheckBox,
    scrollingParams,
    dialogId,
    entityId,
    visibleColumns,
    handleSetBooleanMode,
    handleSetGlobalFilter,
    isBooleanSearchMode,
    defaultFilter,
  } = useContext(PageContext)

  const [checked, setChecked] = useState(columnNameCheckBox[column.code])

  useEffect(() => setChecked(columnNameCheckBox[column.code]), [columnNameCheckBox[column.code]])

  useSubscribeEvent(
    EVENT_NAME.GLOBAL_CHECKBOX_IN_COLUMN,
    (data: { detail: GLOBAL_CHECKBOX_IN_COLUMN_EVENT_DETAIL }) => {
      if (entityId !== data.detail.entityId || dialogId !== data.detail.dialogId) {
        return
      }

      Object.keys(data.detail.columnNameCheckbox).forEach(el => {
        if (el === params.field) {
          setChecked(data.detail.columnNameCheckbox[el])
        }
      })
    }
  )

  const getDisabled = () => {
    if (column) {
      return (
        !getIsAvailableRuleByType(column, searchRule.name) ||
        column.restrictions?.quickSearchPermission === 'false'
      )
    }

    return false
  }

  const showCheckBox = (column: ConfiguredColumn): boolean => {
    return Boolean(column?.columnToSort) && !getDisabled()
  }

  const handleClick: MouseEventHandler<HTMLButtonElement> = e => {
    e.stopPropagation()
    const target = e?.target as typeof e.target & { checked: boolean }
    const checkedCheckbox: boolean = target.checked

    const { page, ...requestParams } = scrollingParams

    let columnNameCheckboxInHandler = columnNameCheckBox
    const sortKeysColumnCheckBoxInHandlers = sortKeysColumnCheckBox

    // Блок управления режимом поиска (строка/boolean) (начало)
    const column = visibleColumns.find(column => column.code === params.field)
    let wasSearchModeChanged = false
    // Логика для изменения формата поиска со строки на boolean
    if (!isBooleanSearchMode) {
      if (column && column.valueType === FIELD_VALUE_TYPE.BOOLEAN) {
        const booleanColumns = visibleColumns.filter(column => {
          return column.valueType !== FIELD_VALUE_TYPE.BOOLEAN
        })
        const newColumnNameCheckbox: Record<string, boolean> = {}

        booleanColumns.forEach(column => {
          if (!column.columnToSort) {
            return
          }

          sortKeysColumnCheckBoxInHandlers[column.columnToSort] = false
        })

        visibleColumns.forEach(viewColumn => {
          const columnCheckboxValue =
            viewColumn.valueType === FIELD_VALUE_TYPE.BOOLEAN && viewColumn.code === column?.code

          newColumnNameCheckbox[viewColumn.code] = columnCheckboxValue
        })

        columnNameCheckboxInHandler = newColumnNameCheckbox

        if (checkedCheckbox) {
          handleSetBooleanMode(true)
          handleSetGlobalFilter('', true)
          wasSearchModeChanged = true
        }
      }
    }

    // Логика для изменения формата поиска с boolean на строку
    if (isBooleanSearchMode) {
      if (column && column.valueType !== FIELD_VALUE_TYPE.BOOLEAN) {
        const nonBooleanColumns = visibleColumns.filter(column => {
          return column.valueType === FIELD_VALUE_TYPE.BOOLEAN
        })
        const newColumnNameCheckbox: Record<string, boolean> = {}

        nonBooleanColumns.forEach(column => {
          if (!column.columnToSort) {
            return
          }

          sortKeysColumnCheckBoxInHandlers[column.columnToSort] = false
        })

        visibleColumns.forEach(viewColumn => {
          const columnCheckboxValue =
            viewColumn.valueType !== FIELD_VALUE_TYPE.BOOLEAN && viewColumn.code === column?.code

          newColumnNameCheckbox[viewColumn.code] = columnCheckboxValue
        })

        columnNameCheckboxInHandler = newColumnNameCheckbox

        handleSetBooleanMode(false)
        handleSetGlobalFilter('', true)
        wasSearchModeChanged = true
      }
    }

    // Блок управления режимом поиска (строка/boolean) (конец)
    const checkboxInColumnForSearchingDetail: CHECKBOX_IN_COLUMN_FOR_SEARCHING_EVENT_DETAIL = {
      sortKeys: sortKeysColumnCheckBoxInHandlers,
      titleColumns: columnNameCheckboxInHandler,
      columnCode: params.field,
      columnName: params.colDef.headerName,
      sortKey: params.colDef.sortKey || '',
      value: checkedCheckbox,
      // Данное условие для searchValue нужно для избежания 400 ошибки при отправке данных в неправильном
      // формате. Если мы меняли режим поиска (boolean и "обычный"), то searchValue просто сбрасывается
      searchValue: wasSearchModeChanged ? '' : valueSearchFilter,
      searchRule,
      scrollingParams: requestParams,
      entityId,
      dialogId,
      wasSearchModeChanged,
      visibleColumns,
      defaultFilter,
    }

    publish(EVENT_NAME.CHECKBOX_IN_COLUMN_FOR_SEARCHING, checkboxInColumnForSearchingDetail)
    setChecked(prev => !prev)
  }

  const sortWidth = 20
  const titleCheckbox = getDisabled()
    ? t('columnInputs.quickSearch.disabledColumnRule', {
        name: searchRule.name.toLowerCase(),
      })
    : undefined
  const showQsCheckbox = hasQuickSearch && showCheckBox(column)

  const quickSearchCheckbox = showQsCheckbox ? (
    <Checkbox
      checked={checked}
      disabled={getDisabled()}
      size={'1rem'}
      title={titleCheckbox}
      sx={{
        flexShrink: 0,
        input: {
          size: '1rem',
          zIndex: theme => theme.zIndex.modal + 15,
        },
        color: theme => theme.palette.grey['800'],
        '&.Mui-checked': {
          color: theme => theme.palette.grey['800'],
        },
        '&.MuiCheckbox-root': {
          padding: 0,
        },
        '&.Mui-disabled': {
          color: theme => theme.palette.grey['400'],
          pointerEvents: 'fill',
        },
      }}
      onChange={e => e.stopPropagation()}
      onClick={handleClick}
    />
  ) : null

  const headerTitle = (
    <Box
      onMouseLeave={handleCloseTooltip}
      onMouseOver={(event: MouseEvent<HTMLElement>) =>
        !showEditMarker && handleOpenTooltip(event, true)
      }
    >
      {hasWrapHeaderText && isEnabledLocalParameters ? (
        params.colDef.headerName
      ) : (
        <GridColumnHeaderTitle columnWidth={columnHeaderTextWidth} label={column.title!} />
      )}
    </Box>
  )

  if (hasWrapHeaderText && isEnabledLocalParameters) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          gap: 0.5,
          pr: isSortableField ? `${sortWidth}px` : 0,
        }}
      >
        {showEditMarker && (
          <Box sx={{ flexShrink: 0, marginRight: 1.5 }}>
            <Edit sx={{ fontSize: '14px', color: '#000' }} />
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            wordBreak: 'break-all',
            whiteSpace: 'pre-wrap',
          }}
        >
          {headerTitle}
        </Box>
        {quickSearchCheckbox}
      </Box>
    )
  }

  return (
    <>
      {showEditMarker && (
        <Box sx={{ marginRight: 1.5 }}>
          <Edit sx={{ fontSize: '14px', color: '#000' }} />
        </Box>
      )}
      {headerTitle}
      {quickSearchCheckbox && (
        <Box sx={{ zIndex: 2, flexShrink: 0 }}>{quickSearchCheckbox}</Box>
      )}
    </>
  )
}
